import React, { useState, useCallback, useMemo } from 'react';
import moment from 'moment';
import { FileText, ExternalLink } from 'lucide-react';
import T from 'Components/Helpers/translate';
import AgentApprovalsResource from 'Resources/AgentApprovals';
import ProposalCardShell, { type StatusPill } from 'App/shadcn/pages/AIWorkspace/components/ProposalCardShell';
import { executionFailureMessage } from './approvalAdapters';
import { openCrmEntity, canOpenCrmEntity, crmEntityLabelKey } from './openCrmEntity';
import type { OfferDraft, OfferDraftLineItem } from './types';

type Props = {
	draft: OfferDraft;
	onUpdate?: (update: Partial<OfferDraft>) => void;
	/** Fully inert render — no Create / Cancel, used by the trace drawer for a
	 *  resolved approval. */
	readOnly?: boolean;
};

const formatAmount = (value: number, currency: string): string =>
	`${value.toLocaleString('en-US', { maximumFractionDigits: 2 })} ${currency}`.trim();

/** Line total only when a unit price was given; otherwise the price is the
 *  catalogue default, which we don't have on the draft, so we can't total it. */
const lineTotal = (p: OfferDraftLineItem): number | null =>
	typeof p.unitPrice === 'number' ? p.unitPrice * p.quantity * (1 - (p.discount ?? 0)) : null;

const offerTotal = (products: OfferDraftLineItem[]): number | null => {
	if (!products.length || products.some(p => typeof p.unitPrice !== 'number')) return null;
	return products.reduce((sum, p) => sum + (lineTotal(p) ?? 0), 0);
};

/** Build the single-line label for a line item (name, qty, unit price,
 *  discount) as a plain string so the JSX carries no bare text literals. */
const lineLabel = (p: OfferDraftLineItem, currency: string): string => {
	const parts = [p.productName || `#${p.productId}`, String(p.quantity)];
	if (typeof p.unitPrice === 'number') parts.push(`× ${formatAmount(p.unitPrice, currency)}`);
	if (p.discount) parts.push(`−${Math.round(p.discount * 100)}%`);
	return parts.join(' · ');
};

function statusToPill(status: OfferDraft['status']): StatusPill | null {
	switch (status) {
		case 'saving':
			return { label: T('assistant.offerCreating') || 'Creating…', tone: 'saving' };
		case 'saved':
			return { label: T('assistant.offerCreated') || 'Created', tone: 'ok' };
		case 'cancelled':
			return { label: T('assistant.cancelled') || 'Cancelled', tone: 'muted' };
		case 'error':
			return { label: T('default.failed') || 'Failed', tone: 'error' };
		case 'pending':
		default:
			return null;
	}
}

const OfferDraftCard = ({ draft, onUpdate, readOnly = false }: Props) => {
	const [busy, setBusy] = useState(false);

	const isCreating = draft.status === 'saving';
	const resolved = draft.status === 'saved' || draft.status === 'cancelled';
	const errored = draft.status === 'error';
	const currency = draft.currency;

	const handleCreate = useCallback(async () => {
		if (!onUpdate || !draft.approvalId) return;
		setBusy(true);
		onUpdate({ status: 'saving' });
		try {
			// Line items aren't editable in the card, so there's no modified
			// payload — the executor runs the original draftOffer input.
			const { approval } = await AgentApprovalsResource.approve(draft.approvalId);
			const failure = executionFailureMessage(
				approval.resultPayload as Record<string, unknown> | null | undefined
			);
			if (failure !== null) {
				onUpdate({ status: 'error', error: failure || T('default.failed') || 'Failed' });
			} else {
				onUpdate({
					status: 'saved',
					resultEntityType: approval.resultEntityType,
					resultEntityId: approval.resultEntityId
				});
			}
		} catch (err) {
			onUpdate({ status: 'error', error: err instanceof Error ? err.message : 'Failed' });
		} finally {
			setBusy(false);
		}
	}, [draft.approvalId, onUpdate]);

	const handleCancel = useCallback(async () => {
		if (!onUpdate) return;
		if (draft.approvalId) {
			try {
				await AgentApprovalsResource.reject(draft.approvalId, 'cancelled_in_chat');
			} catch {
				/* non-fatal — local state still flips to cancelled */
			}
		}
		onUpdate({ status: 'cancelled' });
	}, [draft.approvalId, onUpdate]);

	const handleRetry = useCallback(() => {
		onUpdate?.({ status: 'pending', error: undefined });
	}, [onUpdate]);

	// WEB-9156 — an approved offer creates a real order, but the card gave the
	// user no way to reach it: it stored resultEntityType/resultEntityId and
	// rendered nothing, so the agent was left narrating a navigation path. Same
	// "Open" affordance WriteCard already shows, via the same resolver.
	const { resultEntityType, resultEntityId } = draft;
	const showViewLink =
		draft.status === 'saved' &&
		!!resultEntityType &&
		typeof resultEntityId === 'number' &&
		Number.isFinite(resultEntityId) &&
		resultEntityId > 0 &&
		canOpenCrmEntity(resultEntityType);
	const handleOpenEntity = useCallback(() => {
		if (!showViewLink || !resultEntityType || resultEntityId == null) return;
		openCrmEntity(resultEntityType, resultEntityId);
	}, [showViewLink, resultEntityType, resultEntityId]);
	const viewEntityLabel = useMemo(() => {
		if (!resultEntityType) return null;
		const key = crmEntityLabelKey(resultEntityType);
		return (key && T(key)) || resultEntityType;
	}, [resultEntityType]);
	const headerAction =
		showViewLink && viewEntityLabel ? (
			<button
				type="button"
				className="ws-btn ws-btn-secondary ws-prop-mini-btn"
				onClick={handleOpenEntity}
				title={T('default.entity.view', { entity: viewEntityLabel })}
			>
				<ExternalLink style={{ width: 12, height: 12 }} />
				<span>{T('default.open')}</span>
			</button>
		) : null;

	const total = offerTotal(draft.products);
	const titleParts = [T('assistant.offerDraftTitle') || 'Offer'];
	if (total != null) titleParts.push(formatAmount(total, currency));
	const title = titleParts.join(' · ');

	const validUntilLabel = draft.validUntil ? moment(draft.validUntil).format('L') : '';

	const body = (
		<div className="pr-email-body">
			{errored && draft.error && <div className="pr-email-error">{draft.error}</div>}
			<ul className="pr-offer-lines">
				{draft.products.map((p, idx) => {
					const lt = lineTotal(p);
					return (
						<li key={idx}>
							<span>{lineLabel(p, currency)}</span>
							{lt != null && <span className="pr-offer-line-total">{formatAmount(lt, currency)}</span>}
						</li>
					);
				})}
			</ul>
			<dl className="pr-email-fields">
				<dt>{T('assistant.offerValidUntil') || 'Valid until'}</dt>
				<dd>{validUntilLabel}</dd>
				{draft.notes && (
					<>
						<dt className="pr-email-label-multiline">{T('default.notes') || 'Notes'}</dt>
						<dd>{draft.notes}</dd>
					</>
				)}
			</dl>
		</div>
	);

	const canApprove = !readOnly && !resolved && (errored || (!isCreating && draft.products.length > 0));

	return (
		<div className="pr-email-card-wrapper">
			<ProposalCardShell
				icon={FileText}
				title={title}
				chips={draft.createdDate ? [draft.createdDate] : undefined}
				statusPill={statusToPill(draft.status)}
				headerAction={headerAction}
				body={body}
				busy={busy || isCreating}
				resolved={resolved}
				approveLabel={errored ? T('assistant.retry') : T('assistant.offerCreate') || 'Create offer'}
				rejectLabel={T('cancel') || T('assistant.cancel')}
				onApprove={canApprove ? (errored ? handleRetry : handleCreate) : undefined}
				onReject={readOnly || resolved || errored ? undefined : handleCancel}
			/>
		</div>
	);
};

export default OfferDraftCard;
